import {
    Component,
    OnInit,
    ChangeDetectionStrategy,
    Input,
    Output,
    EventEmitter,
    OnChanges,
    SimpleChanges,
    OnDestroy,
    ChangeDetectorRef,
} from "@angular/core";
import {
    trigger,
    state,
    style,
    transition,
    animate,
} from "@angular/animations";
import { TenantModel } from "@core/models/auth/tenant.model";
import { HostListener } from "@angular/core";
import { Navigate } from "@ngxs/router-plugin";
import { Select, Store } from "@ngxs/store";
import {
    ClearTokenAction,
    ClearLastUserAction,
} from "@ngxs-store/auth-store/auth.actions";
import { MatDialog } from "@angular/material/dialog";
import { ConfirmationDialogComponent } from "@shared/components/confirmation-dialog/confirmation-dialog.component";
import { ClearSelectedTourAction } from "@ngxs-store/managed-tour-store/managed-tour.actions";
import { Observable, Subscription } from "rxjs";
import { filter } from "rxjs/operators";
import { DashboardModel } from "@core/services/api.service";
import { LoadListOfOpenChatsAction } from "@ngxs-store/customs-store/customs.actions";
import { CustomsState } from "@ngxs-store/customs-store/customs.state";
import { Router, NavigationEnd } from "@angular/router";

@Component({
    selector: "app-layout-toolbar",
    templateUrl: "./layout-toolbar.component.html",
    styleUrls: ["./layout-toolbar.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
    animations: [
        trigger("rotatedState", [
            state("default", style({ transform: "rotate(0)" })),
            state("rotated", style({ transform: "rotate(-360deg)" })),
            transition("rotated => default", animate("400ms ease-out")),
            transition("default => rotated", animate("400ms ease-in")),
        ]),
    ],
})
export class LayoutToolbarComponent implements OnInit, OnChanges, OnDestroy {
    @Input() title: string;
    @Input() showMenu: boolean;
    state = "default";
    isCustomsListingPage = false;
    isCustomsOpenMessages = false;
    isManagedChatsActive = false;
    isChatVisible = false;
    private routerEventsSubscription: Subscription;

    private readonly customsRoutes = [
        '/customs-listing',
    ];

    private readonly chatVisibleRoutes = [
        '/tours',
        '/customs-listing',
    ];
    @Input() theme: string;
    @Input() tenants: TenantModel[];
    @Input() currentTenant: TenantModel;
    @Input() signalRConnected: boolean;
    @Output() openMenuClick = new EventEmitter();
    @Output() closeMenuClick = new EventEmitter();
    @Output() openProfileClick = new EventEmitter();
    @Output() disconnectSignalRClicked = new EventEmitter();
    @Output() openSignalRDialogClicked = new EventEmitter();
    @Output() signalRConnectionStateChanged = new EventEmitter<boolean>();
    @Output() tenantSelectionChanged = new EventEmitter<TenantModel>();
    @Select(CustomsState.getHasOpenMessages)
    hasOpenChatsList$: Observable<DashboardModel[]>;
    @Select(CustomsState.getHasNewMessagesForManagedTours)
    hasNewCustomsMessages$: Observable<boolean>;

    constructor(private store: Store, private dialog: MatDialog, private router: Router, private cdr: ChangeDetectorRef) {}

    ngOnInit() {
        this.store.dispatch(new LoadListOfOpenChatsAction());
        this.updateChatModeForRoute(this.router.url);
        this.routerEventsSubscription = this.router.events
            .pipe(filter((event) => event instanceof NavigationEnd))
            .subscribe((event: NavigationEnd) => {
                this.updateChatModeForRoute(event.urlAfterRedirects);
            });
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.showMenu) {
            this.rotateMenuButton();
        }

        if (changes.signalRConnected) {
            this.signalRConnectionStateChanged.emit(this.signalRConnected);
        }
    }

    switchTenant(tenant: TenantModel) {
        this.tenantSelectionChanged.emit(tenant);
    }

    openMenu() {
        this.openMenuClick.emit();
    }

    closeMenu() {
        this.closeMenuClick.emit();
    }

    openProfile() {
        this.openProfileClick.emit();
    }

    logTicket() {
        this.store.dispatch(
            new Navigate(["/log-tickets", { openDialog: true }])
        );
    }

    openManagedChats() {
        if (this.isManagedChatsActive) {
            localStorage.setItem('searchTerm', '');
            this.store.dispatch(new Navigate(["/tours", { filter: "All jobs" }]));
        } else {
            this.store.dispatch(new Navigate(["/tours", { filter: "Open Messages" }]));
        }
    }

    HasMessagesFilter() {
        if (this.isCustomsOpenMessages) {
            localStorage.setItem('searchTerm', '');
            this.store.dispatch(new Navigate(["/customs-listing", { filter: "All Jobs" }]));
        } else {
            this.store.dispatch(new Navigate(["/customs-listing", { filter: "Open Messages" }]));
        }
    }

    ngOnDestroy() {
        if (this.routerEventsSubscription) {
            this.routerEventsSubscription.unsubscribe();
        }
    }

    clearCacheAndLogout() {
        const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
            data: {
                modalTitle: "Clear All Data",
                buttonType: "ClearData",
                confirmationText:
                    "Are you sure you want to clear cache and logout?",
                subConfirmationText:
                    "This will clear all cached data, cookies, and stored information. You will need to log in again.",
            },
        });

        dialogRef.afterClosed().subscribe((result) => {
            if (result) {
                this.performClearCacheAndLogout();
            }
        });
    }

    private performClearCacheAndLogout() {
        this.clearAllData();

        this.forceClearLocalStorage();

        setTimeout(() => {
            this.disableNgxsStorage();

            this.store.dispatch(new ClearLastUserAction());
            this.store.dispatch(new ClearSelectedTourAction());

            setTimeout(() => {
                this.store.dispatch(new ClearTokenAction());
                this.forceReload();
            }, 200);
        }, 100);
    }

    private clearAllData(): void {
        if ("caches" in window) {
            caches.keys().then((names) => {
                names.forEach((name) => {
                    caches.delete(name);
                });
            });
        }

        sessionStorage.clear();

        this.clearAllCookies();

        if ("indexedDB" in window) {
            indexedDB.databases().then((databases) => {
                databases.forEach((db) => {
                    indexedDB.deleteDatabase(db.name);
                });
            });
        }

        if ("serviceWorker" in navigator) {
            navigator.serviceWorker.getRegistrations().then((registrations) => {
                registrations.forEach((registration) => {
                    registration.unregister();
                });
            });
        }

        if ("applicationCache" in window) {
            (window as any).applicationCache.clear();
        }
    }

    private forceClearLocalStorage(): void {
        const keys = Object.keys(localStorage);
        keys.forEach((key) => {
            localStorage.removeItem(key);
        });

        const ngxsKeys = [
            "ui",
            "ManagedTours",
            "MyTours.selectedTourIds",
            "Auth",
            "Users",
            "Tenants",
            "Lookup",
            "UpdateType",
            "Vehicles",
            "ControlTower",
            "Customs",
            "Dashboards",
            "Reports",
            "Roles",
            "Audit",
            "Forms",
            "LogTickets",
            "EmailTrigger",
            "DataSourceConfigs",
            "WorkSplit",
            "Customer",
            "Transport",
            "UpdateType",
        ];

        ngxsKeys.forEach((key) => {
            localStorage.removeItem(key);
        });

        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (
                key &&
                (key.toLowerCase().includes("auth") ||
                    key.toLowerCase().includes("token") ||
                    key.toLowerCase().includes("user") ||
                    key.toLowerCase().includes("login") ||
                    key.toLowerCase().includes("session"))
            ) {
                localStorage.removeItem(key);
            }
        }
    }

    private disableNgxsStorage(): void {
        const ngxsStorageKeys = [
            "auth",
            "ui",
            "ManagedTours",
            "MyTours.selectedTourIds",
            "MyTours",
        ];

        ngxsStorageKeys.forEach((key) => {
            if (key === "auth") {
                localStorage.setItem(
                    key,
                    JSON.stringify({
                        isLoading: false,
                        lastLoggedInUser: {
                            username: "",
                            firstName: "",
                            surname: "",
                        },
                        hasAuthenticatorConfigured: false,
                        pinRequestModel: null,
                        has2FAEnabled: false,
                        resetPasswordSuccess: false,
                        isAccountLocked: null,
                        qrCodeSetupModel: null,
                        decodedToken: {
                            firstName: "",
                            expires: null,
                            roles: [],
                            surname: "",
                            token: "",
                            username: "",
                            uid: "",
                            tenants: [],
                        },
                        tenant: {
                            id: "",
                            name: "",
                        },
                    })
                );
            } else if (key === "ui") {
                localStorage.setItem(
                    key,
                    JSON.stringify({
                        showMenu: false,
                        theme: "light-theme",
                        isLoading: false,
                    })
                );
            } else if (key === "ManagedTours") {
                localStorage.setItem(
                    key,
                    JSON.stringify({
                        isLoading: true,
                        tourList: [],
                        adminTours: [],
                        adminTourList: null,
                        filteredTourList: [],
                        filteredAdminTourList: [],
                        selectedCategory: "",
                        selectedDepartments: [],
                        selectedTypes: [],
                        selectedOwningUsers: [],
                        signalRHandlersRegistered: false,
                        tourHistory: [],
                        tourHistoryList: null,
                        tourUpdateHistory: [],
                        selectedTour: null,
                        notes: [],
                        route: null,
                        locked: null,
                        name: null,
                        loggedIssues: [],
                        selectedModes: [],
                    })
                );
            } else if (key === "MyTours.selectedTourIds") {
                localStorage.setItem(key, JSON.stringify([]));
            }
        });

        setTimeout(() => {
            ngxsStorageKeys.forEach((key) => {
                localStorage.removeItem(key);
            });
        }, 50);
    }

    private forceReload(): void {
        if ("location" in window) {
            const currentUrl = window.location.href;
            const separator = currentUrl.includes("?") ? "&" : "?";
            const cacheBuster = `_cb=${Date.now()}`;
            window.location.href = currentUrl + separator + cacheBuster;
        }
    }

    private clearAllCookies(): void {
        const cookies = document.cookie.split(";");

        cookies.forEach((cookie) => {
            const eqPos = cookie.indexOf("=");
            const name =
                eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();

            if (name) {
                const paths = [
                    "/",
                    "/auth",
                    "/api",
                    "",
                    "/dashboard",
                    "/features",
                ];
                const domains = [
                    window.location.hostname,
                    "." + window.location.hostname,
                    "",
                    "localhost",
                ];

                paths.forEach((path) => {
                    domains.forEach((domain) => {
                        const domainPart = domain ? `;domain=${domain}` : "";
                        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=${path}${domainPart}`;
                        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=${path}${domainPart};secure`;
                        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=${path}${domainPart};httponly`;
                        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=${path}${domainPart};secure;httponly`;
                    });
                });
            }
        });

        const commonCookieNames = [
            "token",
            "auth",
            "session",
            "user",
            "login",
            "remember",
            "refresh",
            "access_token",
            "id_token",
            "auth_token",
            "session_id",
            "user_id",
            "ASP.NET_SessionId",
            "__RequestVerificationToken",
            ".AspNetCore.Identity.Application",
        ];

        commonCookieNames.forEach((cookieName) => {
            const paths = ["/", "/auth", "/api", "", "/dashboard", "/features"];
            const domains = [
                window.location.hostname,
                "." + window.location.hostname,
                "",
                "localhost",
            ];

            paths.forEach((path) => {
                domains.forEach((domain) => {
                    const domainPart = domain ? `;domain=${domain}` : "";
                    document.cookie = `${cookieName}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=${path}${domainPart}`;
                    document.cookie = `${cookieName}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=${path}${domainPart};secure`;
                    document.cookie = `${cookieName}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=${path}${domainPart};httponly`;
                    document.cookie = `${cookieName}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=${path}${domainPart};secure;httponly`;
                });
            });
        });
    }

    rotateMenuButton() {
        this.state = this.showMenu ? "rotated" : "default"; // Fix rotation logic
    }

    disconnectSignalR() {
        this.disconnectSignalRClicked.emit();
    }

    openSignalRDialog() {
        this.openSignalRDialogClicked.emit();
    }

    isScreenSmallerThan600px(): boolean {
        return window.innerWidth < 600;
    }

    @HostListener("window:resize", ["$event"])
    onResize(event: any): void {
        // Close the menu when the screen is resized below 600px
        if (this.isScreenSmallerThan600px()) {
            this.closeMenu();
        }
    }

    private updateChatModeForRoute(url: string) {
        const path = url.split(';')[0].split('?')[0];
        this.isCustomsListingPage = this.customsRoutes.some(r => path.startsWith(r));
        this.isCustomsOpenMessages = this.isCustomsListingPage && (url.includes("filter=Open Messages") || url.includes("filter=Open%20Messages"));
        this.isManagedChatsActive = !this.isCustomsListingPage && (url.includes("filter=Open Messages") || url.includes("filter=Open%20Messages"));
        this.isChatVisible = this.chatVisibleRoutes.some(r => path.startsWith(r));
        this.cdr.markForCheck();
    }
}
